import { FileText } from "lucide-react";
import { type InputBinding } from "../../../../../models/audit/analyticsColumn";
import { type AnalyticsFileParameter } from "../../../../../models/audit/analyticsWorkflow";
import { INPUT_BINDING_COPY } from "../../../../../lib/analyticsFeeds";
import { PaneSection } from "./PaneSection";

/**
 * The name Optro Analytics gives every file input its author has not named.
 *
 * Shown under each file it would say nothing about which slot the file fills,
 * so a parameter carrying it is drawn with no name at all.
 */
const UNNAMED_FILE_INPUT = "file input";

/**
 * The parameter's own name, or null where it has none worth showing.
 *
 * Never the parameter key: that is Optro Analytics' id for the input, not a
 * name anyone gave it.
 */
function parameterName(parameter: AnalyticsFileParameter): string | null {
  const label = parameter.label?.trim() ?? "";
  if (label === "" || label.toLowerCase() === UNNAMED_FILE_INPUT) {
    return null;
  }
  return label;
}

interface InputParametersSectionProps {
  /**
   * The workflow's declared file parameters, re-projected from its activated
   * version on every read. The name on a row is this projection's, never a
   * filename — the projection holds no expected filename and none should be
   * inferred from one.
   */
  parameters: AnalyticsFileParameter[];
  /** What Midship bound to each parameter, keyed by parameter key. */
  bindings: Record<string, InputBinding>;
  /**
   * True where either read behind this section failed.
   *
   * The section's absence is the claim that the workflow declares no file
   * parameters, and a failed read cannot support it; neither can a row drawn
   * without its binding, which would name an unfilled slot for a parameter that
   * may well be filled.
   */
  unreadable: boolean;
  /** Filenames of the session's documents, by document id. */
  documentNames: ReadonlyMap<string, string>;
}

/**
 * What filled this parameter, as the row names it.
 *
 * A document binding names a file and the other two name a rule for finding
 * one — whichever file the test table marks, or the sample's own row — so those
 * two are stated as the rule they are rather than resolved to a filename that
 * would be right for one sample and wrong for the next.
 */
function fillerName(
  binding: InputBinding | undefined,
  documentNames: ReadonlyMap<string, string>,
): string {
  if (binding === undefined) return INPUT_BINDING_COPY.unfilled;
  switch (binding.source) {
    case "document":
      return (
        documentNames.get(binding.documentId) ??
        INPUT_BINDING_COPY.filledByAnotherFile
      );
    case "sample_export":
      return INPUT_BINDING_COPY.filledBySampleExport;
    case "sample_row":
      return INPUT_BINDING_COPY.filledBySampleRow;
  }
}

/**
 * One row per file parameter: what filled it, and the slot's name where its
 * author gave it one.
 *
 * **Two sources, and the row keeps them apart.** The filename is Midship's —
 * the document an auditor bound — and the label is the workflow's, projected
 * from its parameter schema at read time. Nothing here is an "expected
 * filename": the row names what answered the slot, never a file to go and find.
 *
 * **A row carries no star.** The starrable unit is a workflow step, and its
 * star lives on the citation anchored to that step's card. A bound document is
 * a document; the evidence is what the run did with it, and that is on the
 * step.
 *
 * **Absent means the workflow declares no file parameters; it never means the
 * declarations could not be read.** A failed read keeps the heading and says so,
 * because the section that vanishes is making the first claim and only one of
 * the two is true.
 */
export function InputParametersSection({
  parameters,
  bindings,
  unreadable,
  documentNames,
}: InputParametersSectionProps) {
  if (unreadable) {
    return (
      <PaneSection title="INPUT PARAMETERS">
        <p className="text-sm text-font-secondary">
          This workflow's input parameters could not be read.
        </p>
      </PaneSection>
    );
  }

  if (parameters.length === 0) return null;

  return (
    <PaneSection title="INPUT PARAMETERS">
      <ul className="space-y-2">
        {parameters.map((parameter) => {
          const name = parameterName(parameter);
          return (
            <li
              key={parameter.paramKey}
              className="flex items-center gap-3 rounded-lg border border-stone-200 bg-white px-3 py-2.5 text-sm"
            >
              <FileText className="h-4 w-4 shrink-0 text-font-secondary" />
              <div className="min-w-0 flex-1">
                <div className="truncate">
                  {fillerName(bindings[parameter.paramKey], documentNames)}
                </div>
                {name !== null && (
                  <div className="truncate text-xs text-font-secondary">
                    {name}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </PaneSection>
  );
}
